import { FC, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import useStyles from "./styles";
import { useGetAdminPlans } from "../../api/billing";
import {
  TDiscountType,
  TPromoDuration,
  useCreatePromoCode,
} from "../../api/billingAdmin";

type Props = {
  open: boolean;
  onClose: () => void;
};

/**
 * Create a promo code.
 *
 * Creation only — there is no edit mode for the discount itself, because Stripe
 * coupons are immutable once created. Changing a discount means deactivating
 * the code and issuing a replacement, and the API rejects the alternative
 * rather than letting the local record drift from Stripe.
 */
export const ModalAddPromoCode: FC<Props> = ({ open, onClose }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { plans } = useGetAdminPlans(open);

  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [discountType, setDiscountType] = useState<TDiscountType>("percent");
  const [percentOff, setPercentOff] = useState("20");
  const [amountOff, setAmountOff] = useState("10.00");
  const [duration, setDuration] = useState<TPromoDuration>("once");
  const [durationMonths, setDurationMonths] = useState("3");
  const [maxRedemptions, setMaxRedemptions] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [firstTimeOnly, setFirstTimeOnly] = useState(false);
  const [planIds, setPlanIds] = useState<number[]>([]);

  const createPromo = useCreatePromoCode(() => {
    reset();
    onClose();
  });

  const reset = () => {
    setCode("");
    setName("");
    setDiscountType("percent");
    setPercentOff("20");
    setAmountOff("10.00");
    setDuration("once");
    setDurationMonths("3");
    setMaxRedemptions("");
    setExpiresAt("");
    setFirstTimeOnly(false);
    setPlanIds([]);
  };

  // Mirrors the API's regex. Customers type these by hand, so spaces and
  // punctuation are a support burden rather than a validation nicety.
  const codeError = code.length > 0 && !/^[A-Za-z0-9_-]+$/.test(code);

  const percentError =
    discountType === "percent" &&
    (!Number.isFinite(Number(percentOff)) ||
      Number(percentOff) < 1 ||
      Number(percentOff) > 100);

  const amountCents = useMemo(
    () => Math.round(parseFloat(amountOff.replace(",", ".")) * 100),
    [amountOff]
  );

  const amountError =
    discountType === "amount" &&
    (!Number.isFinite(amountCents) || amountCents < 1);

  const canSubmit =
    code.trim().length > 0 &&
    !codeError &&
    !percentError &&
    !amountError &&
    !createPromo.isPending;

  const handleSubmit = () => {
    if (!canSubmit) return;

    createPromo.mutate({
      code: code.trim().toUpperCase(),
      name: name.trim() || undefined,
      discount_type: discountType,
      percent_off: discountType === "percent" ? Number(percentOff) : undefined,
      amount_off_cents: discountType === "amount" ? amountCents : undefined,
      currency: "eur",
      duration,
      duration_in_months:
        duration === "repeating" ? Number(durationMonths) : undefined,
      max_redemptions: maxRedemptions ? Number(maxRedemptions) : null,
      applies_to_plan_ids: planIds.length ? planIds : null,
      first_time_only: firstTimeOnly,
      expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} className={classes.dialog} fullWidth>
      <DialogTitle>
        {t("billing.newPromoCode", "Nouveau code promo")}
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <div className={classes.infoBox}>
          {t(
            "billing.promoImmutableNote",
            "Le montant, le type et la durée de la remise ne pourront plus être modifiés après création (limitation Stripe). Pour changer une remise, désactivez ce code et créez-en un nouveau."
          )}
        </div>

        <div className={classes.formRow}>
          <TextField
            label={t("billing.code", "Code")}
            value={code}
            // Uppercased on the way in so what the admin sees is exactly what
            // the customer will type and what the API stores.
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            error={codeError}
            helperText={
              codeError
                ? t("billing.codeInvalid", "Lettres, chiffres, - et _ uniquement")
                : t("billing.codeHint", "Ex. LAUNCH20")
            }
            autoFocus
          />
          <TextField
            label={t("billing.promoName", "Nom interne")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("billing.promoNamePlaceholder", "Campagne de lancement")}
          />
        </div>

        <div className={classes.formRow}>
          <FormControl>
            <InputLabel>{t("billing.discountType", "Type de remise")}</InputLabel>
            <Select
              label={t("billing.discountType", "Type de remise")}
              value={discountType}
              onChange={(e) => setDiscountType(e.target.value as TDiscountType)}
            >
              <MenuItem value="percent">
                {t("billing.percent", "Pourcentage")}
              </MenuItem>
              <MenuItem value="amount">
                {t("billing.fixedAmount", "Montant fixe")}
              </MenuItem>
            </Select>
          </FormControl>

          {discountType === "percent" ? (
            <TextField
              label={t("billing.percentOff", "Remise (%)")}
              type="number"
              value={percentOff}
              onChange={(e) => setPercentOff(e.target.value)}
              error={percentError}
              helperText={percentError ? "1 – 100" : undefined}
              inputProps={{ min: 1, max: 100 }}
            />
          ) : (
            <TextField
              label={t("billing.amountOff", "Remise (€)")}
              value={amountOff}
              onChange={(e) => setAmountOff(e.target.value)}
              error={amountError}
              helperText={
                amountError
                  ? t("billing.amountInvalid", "Montant invalide")
                  : undefined
              }
            />
          )}
        </div>

        <div className={classes.formRow}>
          <FormControl>
            <InputLabel>{t("billing.duration", "Durée")}</InputLabel>
            <Select
              label={t("billing.duration", "Durée")}
              value={duration}
              onChange={(e) => setDuration(e.target.value as TPromoDuration)}
            >
              <MenuItem value="once">
                {t("billing.durationOnce", "Une seule facture")}
              </MenuItem>
              <MenuItem value="repeating">
                {t("billing.durationRepeating", "Plusieurs mois")}
              </MenuItem>
              <MenuItem value="forever">
                {t("billing.durationForever", "À vie")}
              </MenuItem>
            </Select>
          </FormControl>

          {duration === "repeating" && (
            <TextField
              label={t("billing.durationMonths", "Nombre de mois")}
              type="number"
              value={durationMonths}
              onChange={(e) => setDurationMonths(e.target.value)}
              inputProps={{ min: 1, max: 36 }}
            />
          )}
        </div>

        <div className={classes.formRow}>
          <TextField
            label={t("billing.maxRedemptions", "Utilisations max.")}
            type="number"
            value={maxRedemptions}
            onChange={(e) => setMaxRedemptions(e.target.value)}
            placeholder={t("billing.unlimited", "Illimité")}
            helperText={t("billing.maxRedemptionsHint", "Vide = illimité")}
            inputProps={{ min: 1 }}
          />
          <TextField
            label={t("billing.expiresAt", "Date d'expiration")}
            type="date"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </div>

        <FormControl>
          <InputLabel shrink>
            {t("billing.appliesToPlans", "Offres éligibles")}
          </InputLabel>
          <Select
            multiple
            displayEmpty
            notched
            label={t("billing.appliesToPlans", "Offres éligibles")}
            value={planIds}
            onChange={(e) => setPlanIds(e.target.value as number[])}
            renderValue={(selected) =>
              (selected as number[]).length === 0
                ? t("billing.allPlans", "Toutes les offres")
                : plans
                    .filter((p) => (selected as number[]).includes(p.id))
                    .map((p) => p.name)
                    .join(", ")
            }
          >
            {plans.map((plan) => (
              <MenuItem key={plan.id} value={plan.id}>
                <Checkbox checked={planIds.includes(plan.id)} />
                {plan.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControlLabel
          control={
            <Checkbox
              checked={firstTimeOnly}
              onChange={(e) => setFirstTimeOnly(e.target.checked)}
            />
          }
          label={t(
            "billing.firstTimeOnly",
            "Réservé à un premier abonnement"
          )}
        />
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button onClick={onClose}>{t("common.cancel", "Annuler")}</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
          startIcon={
            createPromo.isPending ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          {t("billing.createCode", "Créer le code")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalAddPromoCode;
